
import React from 'react';

interface ServiceExample {
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  icon: string;
  color: 'blue' | 'emerald' | 'indigo' | 'rose' | 'violet';
  decorativeType: 'pills' | 'lines' | 'dots';
}

const services: ServiceExample[] = [
  {
    title: "Events & Blog Sites",
    subtitle: "Web Presence",
    description: "A dynamic website that keeps your audience engaged — with an event calendar, blog posts, photo galleries, and RSVP functionality built right in.",
    features: [
      "Event listings & RSVP management",
      "Blog & news publishing",
      "Photo gallery",
      "Mobile-friendly design",
    ],
    icon: "fas fa-calendar-alt",
    color: "blue",
    decorativeType: "pills",
  },
  {
    title: "Members Hub",
    subtitle: "Community Platform",
    description: "A private community space where members log in, access resources, stay up to date with announcements, and manage their own profiles.",
    features: [
      "Member login & profiles",
      "Resource & document library",
      "Access control by role",
      "Announcements & notices",
    ],
    icon: "fas fa-id-badge",
    color: "emerald",
    decorativeType: "lines",
  },
  {
    title: "Group Calendars",
    subtitle: "Scheduling App",
    description: "Shared scheduling made simple — coordinate group activities, track RSVPs, manage sub-groups, and send automated reminders.",
    features: [
      "Shared group calendar",
      "RSVP & attendance tracking",
      "Group & sub-group management",
      "Automated event notifications",
    ],
    icon: "fas fa-calendar-check",
    color: "indigo",
    decorativeType: "dots",
  },
  {
    title: "Food Ordering App",
    subtitle: "Restaurant Platform",
    description: "A branded ordering experience for your restaurant — digital menu, online orders, pickup or delivery options, and real-time order tracking.",
    features: [
      "Digital menu with photos & prices",
      "Online ordering & checkout",
      "Pickup & delivery options",
      "Order status tracking",
    ],
    icon: "fas fa-utensils",
    color: "rose",
    decorativeType: "pills",
  },
  {
    title: "Class & Booking App",
    subtitle: "Booking Platform",
    description: "Let your clients book classes, sessions, or appointments online — manage your schedule, control capacity, send reminders, and accept payments all in one place.",
    features: [
      "Class & session scheduling",
      "Online booking & payments",
      "Capacity & waitlist management",
      "Automated booking reminders",
    ],
    icon: "fas fa-chalkboard-teacher",
    color: "violet",
    decorativeType: "lines",
  },
];

const themes = {
  blue: {
    border: 'hover:border-blue-300',
    glow: 'hover:shadow-blue-500/10',
    text: 'text-blue-600',
    visualBg: 'from-blue-50 to-blue-100/60',
    icon: 'text-blue-400 group-hover:text-blue-600',
    pill: 'bg-blue-200/70 text-blue-700',
    line: 'bg-blue-200',
    check: 'text-blue-500',
    pillLabels: ['Upcoming', 'Events', 'Posts'],
  },
  emerald: {
    border: 'hover:border-emerald-300',
    glow: 'hover:shadow-emerald-500/10',
    text: 'text-emerald-600',
    visualBg: 'from-emerald-50 to-emerald-100/60',
    icon: 'text-emerald-400 group-hover:text-emerald-600',
    pill: 'bg-emerald-200/70 text-emerald-700',
    line: 'bg-emerald-200',
    check: 'text-emerald-500',
    pillLabels: ['Members', 'Resources', 'News'],
  },
  indigo: {
    border: 'hover:border-indigo-300',
    glow: 'hover:shadow-indigo-500/10',
    text: 'text-indigo-600',
    visualBg: 'from-indigo-50 to-indigo-100/60',
    icon: 'text-indigo-400 group-hover:text-indigo-600',
    pill: 'bg-indigo-200/70 text-indigo-700',
    line: 'bg-indigo-200',
    check: 'text-indigo-500',
    pillLabels: ['Schedule', 'Groups', 'RSVP'],
  },
  rose: {
    border: 'hover:border-rose-300',
    glow: 'hover:shadow-rose-500/10',
    text: 'text-rose-600',
    visualBg: 'from-rose-50 to-rose-100/60',
    icon: 'text-rose-400 group-hover:text-rose-600',
    pill: 'bg-rose-200/70 text-rose-700',
    line: 'bg-rose-200',
    check: 'text-rose-500',
    pillLabels: ['Menu', 'Order', 'Delivery'],
  },
  violet: {
    border: 'hover:border-violet-300',
    glow: 'hover:shadow-violet-500/10',
    text: 'text-violet-600',
    visualBg: 'from-violet-50 to-violet-100/60',
    icon: 'text-violet-400 group-hover:text-violet-600',
    pill: 'bg-violet-200/70 text-violet-700',
    line: 'bg-violet-200',
    check: 'text-violet-500',
    pillLabels: ['Book', 'Classes', 'Waitlist'],
  },
};

type Theme = typeof themes['blue'];

const DecorativeLayer: React.FC<{ type: ServiceExample['decorativeType']; theme: Theme }> = ({ type, theme }) => {
  if (type === 'pills') {
    return (
      <>
        <div className={`absolute top-6 left-6 px-3 py-1 rounded-full text-[10px] font-black ${theme.pill} opacity-80`}>{theme.pillLabels[0]}</div>
        <div className={`absolute top-6 right-6 px-3 py-1 rounded-full text-[10px] font-black ${theme.pill} opacity-60`}>{theme.pillLabels[1]}</div>
        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-black ${theme.pill} opacity-50`}>{theme.pillLabels[2]}</div>
      </>
    );
  }
  if (type === 'lines') {
    return (
      <div className="absolute bottom-6 left-8 right-8 flex flex-col gap-2 opacity-40">
        <div className={`h-1.5 rounded-full ${theme.line} w-3/4`}></div>
        <div className={`h-1.5 rounded-full ${theme.line} w-1/2`}></div>
        <div className={`h-1.5 rounded-full ${theme.line} w-2/3`}></div>
      </div>
    );
  }
  return (
    <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 opacity-50">
      {[...Array(5)].map((_, i) => (
        <div key={i} className={`w-2 h-2 rounded-full ${theme.line}`}></div>
      ))}
    </div>
  );
};

const WorkShowcase: React.FC = () => {
  return (
    <section id="work" className="py-24 relative overflow-hidden scroll-mt-24 bg-gradient-to-b from-white via-purple-50/10 to-rose-50/20">
      <div className="container mx-auto px-6 relative z-10">
        <div className="mb-16 text-center">
          <span className="text-blue-600 font-black uppercase tracking-widest text-xs mb-4 block">What We Build</span>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
            Solutions <span className="gradient-text">Built For You</span>
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto font-semibold">
            Every business has a specific need. We build purpose-built web applications designed around how you actually work — not off-the-shelf templates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {services.map((service, index) => {
            const theme = themes[service.color];
            const isWide = index === services.length - 1 && services.length % 2 !== 0;
            return (
              <div
                key={index}
                className={`group relative flex bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-[3rem] overflow-hidden transition-all duration-700 hover:-translate-y-4 shadow-xl shadow-black/5 ${theme.border} ${theme.glow} ${isWide ? 'md:col-span-2 flex-col md:flex-row' : 'flex-col'}`}
              >
                {/* Visual area */}
                <div className={`bg-gradient-to-br ${theme.visualBg} relative overflow-hidden flex items-center justify-center flex-shrink-0 ${isWide ? 'h-52 md:h-auto md:w-72' : 'h-52'}`}>
                  <div className="absolute inset-0 opacity-10 bg-grid"></div>
                  <DecorativeLayer type={service.decorativeType} theme={theme} />
                  <div className="relative z-10 w-24 h-24 bg-white rounded-3xl shadow-lg flex items-center justify-center text-5xl transition-all duration-700 group-hover:scale-125 group-hover:rotate-12">
                    <i className={`${service.icon} ${theme.icon} transition-colors`}></i>
                  </div>
                </div>

                {/* Content */}
                <div className="p-10 flex-grow flex flex-col">
                  <div className={`text-xs font-black uppercase tracking-widest mb-3 ${theme.text}`}>{service.subtitle}</div>
                  <h3 className="text-2xl font-black text-slate-900 mb-4 tracking-tight leading-none">{service.title}</h3>
                  <p className="text-slate-600 text-sm font-bold leading-relaxed mb-8 flex-grow">
                    {service.description}
                  </p>

                  <ul className={`space-y-3 mb-10 ${isWide ? 'md:grid md:grid-cols-2 md:gap-x-8 md:space-y-0' : ''}`}>
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3 text-sm font-bold text-slate-700">
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm ${theme.check}`}>
                          <i className="fas fa-check text-[10px]"></i>
                        </span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    className="inline-flex items-center gap-3 text-slate-900 font-black text-xs uppercase tracking-widest hover:text-blue-600 transition-all group/link"
                  >
                    Get a Quote
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center group-hover/link:bg-blue-600 group-hover/link:text-white transition-all">
                      <i className="fas fa-arrow-right text-[10px]"></i>
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WorkShowcase;
